import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Form, FormGroup, Label, Row, Col, Button, Alert } from 'reactstrap';

import { Switch, CheckboxGroup, NativeSelect, RHFField, rfRules } from '../../../../../components/Field';
import { timeRange, notEmpty, required, min0, max23 } from '../../../../../components/Field/validate';
import { updateScheduler, clearSourceMessage, fetchSourceTables } from '../../../../../actions/synchronisationActions';

const DAY_OPTIONS = [
  { name: 'MON', label: 'Mo' },
  { name: 'TUE', label: 'Tu' },
  { name: 'WED', label: 'We' },
  { name: 'THU', label: 'Th' },
  { name: 'FRI', label: 'Fr' },
  { name: 'SAT', label: 'Sa' },
  { name: 'SUN', label: 'Su' },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => ({ name: i, value: i }));

const FREQUENCY_OPTIONS = [
  { name: 'Every Minute', value: 'ONE_MIN' },
  { name: 'Every 2 Minutes', value: 'TWO_MIN' },
  { name: 'Every 5 Minutes', value: 'FIVE_MIN' },
  { name: 'Every 10 Minutes', value: 'TEN_MIN' },
  { name: 'Every 15 Minutes', value: 'FIFTEEN_MIN' },
  { name: 'Every 30 Minutes', value: 'THIRTY_MIN' },
  { name: 'Every Hour', value: 'ONE_HOUR' },
];

function SchedulerForm() {
  const dispatch = useDispatch();
  const sourceId = useSelector((s) => s.sync.source.currentSourceId);
  const scheduler = useSelector((s) => s.sync.scheduler.scheduler);
  const updating = useSelector((s) => s.sync.scheduler.updating);
  const message = useSelector((s) => s.sync.scheduler.message);
  const error = useSelector((s) => s.sync.scheduler.error);
  const currentStore = useSelector((s) => s.store.one);

  const defaults = () => ({
    isActive: scheduler.isActive,
    days: scheduler.days || [],
    startHour: scheduler.startHour,
    endHour: scheduler.endHour,
    frequency: scheduler.frequency,
  });

  const { control, handleSubmit, reset, getValues, watch } = useForm({ defaultValues: defaults() });
  const enabled = !!watch('isActive');

  useEffect(() => {
    reset(defaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduler]);

  useEffect(() => {
    if (currentStore && currentStore.storeId) dispatch(fetchSourceTables(currentStore.storeId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  const submit = (values) => {
    if (sourceId) dispatch(updateScheduler(sourceId, values));
  };

  const onDismiss = () => dispatch(clearSourceMessage());

  return (
    <Form className="col-sm-12 col-md-6 col-lg-6" onSubmit={handleSubmit(submit)}>
      <FormGroup className="d-flex flex-row align-items-baseline">
        <Label className="col-xs-12 col-sm-4 col-md-4 col-lg-4">Scheduler Status</Label>
        <Col xs={12} sm={8}>
          <RHFField id="schedulerStatus" name="isActive" control={control} component={Switch} />
        </Col>
      </FormGroup>

      <FormGroup className="d-flex flex-row align-items-baseline">
        <Label className="col-xs-12 col-sm-4 col-md-4 col-lg-4">Week days</Label>
        <Col xs={12} sm={8}>
          <RHFField
            name="days"
            control={control}
            component={CheckboxGroup}
            disabled={!enabled}
            options={DAY_OPTIONS}
            rules={enabled ? rfRules([notEmpty], getValues) : {}}
          />
        </Col>
      </FormGroup>

      <FormGroup className="d-flex flex-row align-items-baseline">
        <Label className="col-xs-12 col-sm-4 col-md-4 col-lg-4">Operating Hours</Label>
        <Col xs={12} sm={8}>
          <Row>
            <Col>
              <RHFField
                id="startTime"
                name="startHour"
                control={control}
                component={NativeSelect}
                disabled={!enabled}
                options={TIME_OPTIONS}
                rules={enabled ? rfRules([required, min0, max23], getValues) : {}}
              />
            </Col>
            <Col>
              <RHFField
                id="endTime"
                name="endHour"
                control={control}
                component={NativeSelect}
                disabled={!enabled}
                options={TIME_OPTIONS}
                rules={enabled ? rfRules([timeRange, required, min0, max23], getValues) : {}}
              />
            </Col>
          </Row>
        </Col>
      </FormGroup>

      <FormGroup className="d-flex flex-row align-items-baseline">
        <Label className="col-xs-12 col-sm-4 col-md-4 col-lg-4">Scheduler Frequency</Label>
        <Col className="col-xs-12 col-sm-8 col-md-8 col-lg-8">
          <RHFField
            id="frequency"
            name="frequency"
            control={control}
            component={NativeSelect}
            disabled={!enabled}
            options={FREQUENCY_OPTIONS}
            rules={enabled ? rfRules([required], getValues) : {}}
          />
        </Col>
      </FormGroup>

      {message && (
        <FormGroup>
          <Alert color={error ? 'danger' : 'success'} isOpen={!!message} toggle={onDismiss}>
            {message}
          </Alert>
        </FormGroup>
      )}

      <FormGroup>
        <Button color="primary" disabled={updating}>
          Save
        </Button>
      </FormGroup>
    </Form>
  );
}

export default SchedulerForm;
